const main = document.querySelector('.template-index #MainContent');

if (main && 'IntersectionObserver' in window) {
  const wrapper = main.closest('.page-wrapper');
  const scrollRoot = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) ? wrapper : null;
  const scrollTarget = scrollRoot || window;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const desktopMotion = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const revealSelector = [
    '.pstr-products .pstr-section__head',
    '.pstr-products .pstr-product-grid > .pstr-card',
    '.pstr-products .pstr-product-grid > .pstr-promo',
    '.pstr-products .pstr-track--products',
    '.pstr-products .pstr-section__bottom',
    '.pstr-feature__copy',
    '.pstr-feature__products > .pstr-card',
    '.pstr-media-rail .pstr-track--media',
    '.pstr-reviews .pstr-track--reviews',
    '.pstr-gallery .pstr-track--gallery',
    '.bj-quiz-cta',
    '.bj-home-seo__inner',
  ].join(',');
  const revealed = new WeakSet();
  const pending = new Set();
  const animating = new Set();
  const heroes = new Map();
  let revealObserver;

  function finishReveal(element) {
    element.classList.remove('bj-home-enter');
    animating.delete(element);
  }

  function observeReveals(scope = main) {
    if (reducedMotion.matches) return;
    if (!revealObserver) {
      const reported = new WeakSet();
      const observer = new IntersectionObserver(entries => {
        if (revealObserver !== observer || reducedMotion.matches) return;
        for (const entry of entries) {
          const element = entry.target;
          const firstReport = !reported.has(element);
          reported.add(element);
          if (!entry.isIntersecting || !pending.has(element)) continue;
          observer.unobserve(element);
          pending.delete(element);
          revealed.add(element);
          if (firstReport || document.hidden || reducedMotion.matches || element.contains(document.activeElement)) continue;
          element.classList.add('bj-home-enter');
          animating.add(element);
        }
      }, { root: scrollRoot, rootMargin: '0px 0px 24px 0px', threshold: 0.02 });
      revealObserver = observer;
    }
    scope.querySelectorAll(revealSelector).forEach(element => {
      if (revealed.has(element) || pending.has(element)) return;
      pending.add(element);
      revealObserver.observe(element);
    });
  }

  function stopReveals() {
    revealObserver?.disconnect();
    revealObserver = null;
    pending.clear();
    animating.forEach(finishReveal);
  }

  function observeHero(hero) {
    let disposed = false;
    let inView = false;
    let listening = false;
    let frame = 0;
    let measureNeeded = true;
    let start = 0;
    let end = 1;
    let height = 1;
    const scrollTop = () => scrollRoot ? scrollRoot.scrollTop : window.scrollY;

    const render = () => {
      frame = 0;
      if (!listening) return;
      if (measureNeeded) {
        const bounds = hero.getBoundingClientRect();
        const rootTop = scrollRoot ? scrollRoot.getBoundingClientRect().top + scrollRoot.clientTop : 0;
        const rootHeight = scrollRoot ? scrollRoot.clientHeight : document.documentElement.clientHeight;
        const top = bounds.top - rootTop + scrollTop();
        height = bounds.height;
        start = Math.max(0, top - Math.min(110, rootHeight * .12));
        end = start + Math.max(240, height * .78);
        measureNeeded = false;
      }
      const progress = Math.max(0, Math.min(1, (scrollTop() - start) / (end - start)));
      const mobile = !desktopMotion.matches;
      hero.style.setProperty('--bj-hero-y', `${(height * (mobile ? .045 : .075) * progress).toFixed(2)}px`);
      hero.style.setProperty('--bj-hero-scale', (1 + progress * (mobile ? .16 : .24)).toFixed(4));
      hero.style.setProperty('--bj-hero-shrink', (1 - progress * (mobile ? .08 : .12)).toFixed(4));
      hero.style.setProperty('--bj-hero-radius', `${(progress * (mobile ? 18 : 28)).toFixed(2)}px`);
    };

    const schedule = () => {
      if (listening && !frame) frame = requestAnimationFrame(render);
    };

    const measure = () => {
      measureNeeded = true;
      schedule();
    };

    const sync = () => {
      if (disposed) return;
      const active = inView && !document.hidden;
      if (active === listening) return;
      listening = active;
      hero.toggleAttribute('data-bj-home-parallax-active', active);
      if (active) {
        scrollTarget.addEventListener('scroll', schedule, { passive: true });
        measure();
      } else {
        scrollTarget.removeEventListener('scroll', schedule);
        cancelAnimationFrame(frame);
        frame = 0;
      }
    };

    const observer = new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      sync();
    }, { root: scrollRoot });
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    hero.setAttribute('data-bj-home-parallax', '');
    observer.observe(hero);
    resizeObserver?.observe(hero);
    if (scrollRoot) resizeObserver?.observe(scrollRoot);
    window.addEventListener('resize', measure, { passive: true });
    document.addEventListener('visibilitychange', sync);

    return () => {
      disposed = true;
      listening = false;
      observer.disconnect();
      resizeObserver?.disconnect();
      scrollTarget.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', measure);
      document.removeEventListener('visibilitychange', sync);
      cancelAnimationFrame(frame);
      hero.removeAttribute('data-bj-home-parallax');
      hero.removeAttribute('data-bj-home-parallax-active');
      hero.style.removeProperty('--bj-hero-y');
      hero.style.removeProperty('--bj-hero-scale');
      hero.style.removeProperty('--bj-hero-shrink');
      hero.style.removeProperty('--bj-hero-radius');
    };
  }

  function syncHeroes(scope = main) {
    if (reducedMotion.matches) {
      heroes.forEach(cleanup => cleanup());
      heroes.clear();
      return;
    }
    scope.querySelectorAll('[data-pstr-hero]').forEach(hero => {
      if (!heroes.has(hero)) heroes.set(hero, observeHero(hero));
    });
  }

  for (const eventName of ['animationend', 'animationcancel']) {
    main.addEventListener(eventName, event => {
      if (event.animationName === 'bonjoer-home-enter') finishReveal(event.target);
    });
  }
  main.addEventListener('focusin', event => {
    const element = event.target.closest(revealSelector);
    if (!element || !main.contains(element)) return;
    revealObserver?.unobserve(element);
    pending.delete(element);
    revealed.add(element);
    finishReveal(element);
  });
  reducedMotion.addEventListener('change', () => {
    stopReveals();
    observeReveals();
    syncHeroes();
  });
  desktopMotion.addEventListener('change', () => {
    heroes.forEach(cleanup => cleanup());
    heroes.clear();
    syncHeroes();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) animating.forEach(finishReveal);
  });
  document.addEventListener('shopify:section:load', event => {
    if (!main.contains(event.target)) return;
    observeReveals(event.target);
    syncHeroes(event.target);
  });
  document.addEventListener('shopify:section:unload', event => {
    for (const element of pending) {
      if (!event.target.contains(element)) continue;
      revealObserver?.unobserve(element);
      pending.delete(element);
    }
    for (const element of animating) {
      if (event.target.contains(element)) finishReveal(element);
    }
    for (const [hero, cleanup] of heroes) {
      if (!event.target.contains(hero)) continue;
      cleanup();
      heroes.delete(hero);
    }
  });

  observeReveals();
  syncHeroes();
}

